import { useCallback, useEffect, useRef } from 'react';

/**
 * History trap for page-level overlays (drawers).
 *
 * Contract (see design-system/decisions.md #006):
 * - On open: pushState so browser/Android back closes the overlay first.
 * - requestClose(): at most one history.back(); popstate calls onClose.
 * - With a fullscreen backdrop, do NOT also useClickOutside on the panel for close
 *   (mousedown + click would double-back and leave the page).
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {{ stateKey?: string, interceptPop?: () => boolean }} [options]
 *   interceptPop: if returns true, pop was handled (e.g. exit edit mode) and onClose is skipped.
 * @returns {() => void} requestClose
 */
export default function useOverlayHistory(isOpen, onClose, options = {}) {
  const { stateKey = 'qappOverlay', interceptPop } = options;
  const ownsEntryRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const interceptPopRef = useRef(interceptPop);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    interceptPopRef.current = interceptPop;
  }, [interceptPop]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPop = () => {
      ownsEntryRef.current = false;
      if (interceptPopRef.current?.()) return;
      onCloseRef.current?.();
    };

    window.history.pushState({ [stateKey]: true }, '', window.location.href);
    ownsEntryRef.current = true;
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      // Parent closed without requestClose — drop our fake entry (listener already gone).
      if (ownsEntryRef.current) {
        ownsEntryRef.current = false;
        window.history.back();
      }
    };
  }, [isOpen, stateKey]);

  const requestClose = useCallback(() => {
    if (ownsEntryRef.current) {
      window.history.back();
    } else {
      onCloseRef.current?.();
    }
  }, []);

  return requestClose;
}
